import clsx from 'clsx';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import DataTable from '@components/DataTable';
import LazyImgixImage from '@components/LazyImgixImage';
import { AppContextProvider, Env } from '@contexts/AppContext';
import { ProjectColumnConfigFn, ProjectData } from '@interfaces/projects';
import { getImgixSrc } from '@utils/images';

import TableKey from './TableKey';

interface SportsStreamingDatabaseProps<TData, TValue> {
  data: ProjectData<TData, TValue>;
  env: Env;
  isWebview: boolean;
  projectId: number;
}

const COLUMN_BROADCAST = 'broadcast';
const COLUMN_COMPANY = 'company';
const COLUMN_IMAGE = 'image';
const COLUMN_STREAMING = 'streaming';
const PROJECT_IMAGE_PATH = 'projects/sports-streaming-broadcasting-rights';

const columnConfigFn: ProjectColumnConfigFn = (column) => {
  if (column.id === COLUMN_COMPANY) {
    return {
      ...column,
      header: () => <span className="company">{column.header as string}</span>,
      cell: (info) => {
        const cellVal = info.getValue() as string;
        const imageVal = info.row.getValue(COLUMN_IMAGE) as string;
        const broadcastVal = info.row.getValue(COLUMN_BROADCAST) as string;
        const streamingVal = info.row.getValue(COLUMN_STREAMING) as string;
        return (
          <div className="company flex flex-col items-start justify-start gap-20 xs:flex-row xs:items-center">
            <LazyImgixImage
              alt={cellVal}
              className="h-56 w-56 rounded-full"
              src={getImgixSrc({
                path: `${PROJECT_IMAGE_PATH}/${imageVal}`,
                isEnvDependent: false,
              })}
            />
            <div className="flex flex-col">
              <div className="font-bold">{cellVal}</div>
              {broadcastVal && <div>TV: {broadcastVal}</div>}
              {streamingVal && <div>Streaming: {streamingVal}</div>}
            </div>
          </div>
        );
      },
      id: column.id,
    };
  } else
    return {
      ...column,
      cell: (info) => {
        let cellVal = info.getValue() as string;

        // extract color definitions from cells if present (e.g. "...text color_green")
        const boxColor = cellVal?.split('color_')[1] || '';
        const sharedStyles = clsx(boxColor, 'whitespace-break-spaces');
        cellVal = cellVal?.split('color_')[0];

        // extract heading text from cells if present (e.g. "Heading: Text...")
        const cellValHeadingSplit = cellVal?.split(': ');
        if (!cellValHeadingSplit || cellValHeadingSplit.length === 1) {
          return <span className={sharedStyles}>{ReactHtmlParser(cellVal)}</span>;
        }

        const cellValHeadingText = cellValHeadingSplit[0];
        const cellValText = cellValHeadingSplit[1];

        return (
          <div className={sharedStyles}>
            <div className="font-bold">{cellValHeadingText}</div>
            <div>{ReactHtmlParser(cellValText)}</div>
          </div>
        );
      },
      // table default is 150, just bumping it up to 200 for this project
      size: column.size || 200,
      id: column.id,
    };
};

const SportsStreamingDatabase = <TData, TValue>({
  data,
  env,
  isWebview,
  projectId,
}: SportsStreamingDatabaseProps<TData, TValue>): JSX.Element => {
  return (
    <AppContextProvider env={env} isWebview={isWebview}>
      <TableKey />
      <DataTable
        classNameTd="border-b-grayscale-gray/25 border-r-grayscale-gray/25 has-[.company]:bg-grayscale-beige has-[.company]:border-r-grayscale-gray has-[.green]:bg-secondary-green-mid/[0.15] has-[.red]:bg-primary-magenta/[0.15] has-[.orange]:bg-secondary-brown-mid/[0.15]"
        classNameTh="border-r-grayscale-gray/25 has-[.company]:border-r-grayscale-gray max-xs:has-[.company]:!min-w-[150px]"
        columns={data.columns}
        columnConfigFn={columnConfigFn}
        data={data.data}
        hiddenCols={data.hiddenCols}
        pinnedCols={data.pinnedCols}
        projectId={projectId}
        sortCols={data.sortCols}
        showSearch
      />
    </AppContextProvider>
  );
};

export default SportsStreamingDatabase;
